import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { type EmberChangeset } from 'ember-changeset';
import t from 'ember-intl/helpers/t';
import mult from 'ember-math-helpers/helpers/mult';
import type { WrFormComponentSignature } from 'ember-wiser/components/form';
import ValueWithUnit from 'parog-web/components/value-with-unit';
import Timing from 'parog-web/models/prescriptions/timing';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';
import { type medsInterface } from '../form';
import Dropdown from './dropdown';
interface Args {
  changeset: EmberChangeset;
  form: WrFormComponentSignature<
    EmberChangeset,
    unknown
  >['Blocks']['default'][0];
  medication: medsInterface;
  prefix: (property: string) => string;
}

const doseQuantities = [
  '0.25',
  ...Array.from({ length: 20 }, (_, i) => (0.5 * (i + 1)).toString())
] as const;

const timings = [
  new Timing({ frequency: 1, period: 1, periodUnit: 'd', when: ['morn'] }),
  new Timing({ frequency: 1, period: 1, periodUnit: 'd', when: ['cd'] }),
  new Timing({ frequency: 1, period: 1, periodUnit: 'd', when: ['eve'] }),
  new Timing({ frequency: 2, period: 1, periodUnit: 'd' }),
  new Timing({
    frequency: 2,
    period: 1,
    periodUnit: 'd',
    when: ['morn', 'cd']
  }),
  new Timing({ frequency: 2, period: 1, periodUnit: 'd', when: ['cd', 'eve'] }),
  new Timing({ frequency: 3, period: 1, periodUnit: 'd' }),
  new Timing({ frequency: 4, period: 1, periodUnit: 'd' }),
  new Timing({ frequency: 1, period: 1, periodUnit: 'd' }),
  new Timing({ frequency: 1, period: 4, periodUnit: 'h' }),
  new Timing({ frequency: 1, period: 6, periodUnit: 'h' }),
  new Timing({ frequency: 1, period: 8, periodUnit: 'h' }),
  new Timing({ frequency: 1, period: 12, periodUnit: 'h' }),
  new Timing({ frequency: 1, period: 1, periodUnit: 'wk' }),
  new Timing({ frequency: 2, period: 1, periodUnit: 'wk' }),
  new Timing({ frequency: 3, period: 1, periodUnit: 'wk' }),
  new Timing({ frequency: 4, period: 1, periodUnit: 'wk' }),
  new Timing({ frequency: 5, period: 1, periodUnit: 'wk' }),
  new Timing({ frequency: 6, period: 1, periodUnit: 'wk' }),
  new Timing({ frequency: 1, period: 2, periodUnit: 'd' }),
  new Timing({ frequency: 1, period: 3, periodUnit: 'd' }),
  new Timing({ frequency: 1, period: 4, periodUnit: 'd' }),
  new Timing({ frequency: 1, period: 5, periodUnit: 'd' }),
  new Timing({ frequency: 1, period: 6, periodUnit: 'd' }),
  new Timing({ frequency: 1, period: 2, periodUnit: 'wk' }),
  new Timing({ frequency: 1, period: 3, periodUnit: 'wk' }),
  new Timing({ frequency: 1, period: 1, periodUnit: 'mo' }),
  new Timing({ frequency: 1, period: 2, periodUnit: 'mo' }),
  new Timing({ frequency: 1, period: 3, periodUnit: 'mo' }),
  new Timing({ frequency: 1, period: 4, periodUnit: 'mo' }),
  new Timing({ frequency: 1, period: 6, periodUnit: 'mo' }),
  new Timing({ frequency: 1, period: 1, periodUnit: 'a' }),
  new Timing({ asNeeded: true }),
  new Timing({ isOther: true })
] as const;

const suggestionComments = [
  'Pendant les repas.',
  'Espacer les prises de 12 heures.',
  'À prendre à  6h et à 18h.',
  'À prendre à  7h et à 19h.',
  'À prendre à  8h et à 20h.',
  'À prendre à  9h et à 21h.',
  'À prendre à 10h et à 22h.'
] as const;

export default class dosageColumnComponent extends Component<Args> {
  appendComment = (str: string) => {
    const key = this.args.prefix('comment');
    if (this.args.changeset[key]) {
      this.args.changeset[key] += str;
    } else {
      this.args.changeset[key] = str;
    }
  };

  <template>
    <div class='mb-4 flex gap-2'>
      <Dropdown
        @changeset={{@changeset}}
        @placeholder='Nombre'
        @propertyName={{@prefix 'doseQuantity'}}
        as |dropdown|
      >
        {{#each doseQuantities as |doseQuantity|}}
          <dropdown.Item @key={{doseQuantity}}>
            <div class='flex flex-col gap-2'>
              <div>
                {{t (concat 'prescriptions.doseQuantities.' doseQuantity)}}
              </div>
              <div class='text-grey'>
                {{#if @medication.unit}}
                  (<ValueWithUnit
                    @value={{mult @medication.dose doseQuantity}}
                    @unit={{@medication.unit}}
                  />)
                {{else}}
                  {{mult @medication.dose doseQuantity}}
                {{/if}}
              </div>
            </div>
          </dropdown.Item>
        {{/each}}
      </Dropdown>
      <Dropdown
        @changeset={{@changeset}}
        @placeholder='Fréquence'
        @propertyName={{@prefix 'timing'}}
        as |dropdown|
      >
        {{#each timings as |timing|}}
          <dropdown.Item
            @key={{timing.computedId}}
            @value={{t (concat 'prescriptions.timings.' timing.computedId)}}
          />
        {{/each}}
      </Dropdown>
    </div>
    <div class='flex'>
      <span class='w-full'>
        <@form.Textarea
          rows='2'
          @name={{@prefix 'comment'}}
          placeholder='Précisions'
        />
      </span>
      <div
        {{fuiDropdown}}
        class='ui dropdown'
        data-test-comment-suggestion-dropdown
      >
        <i class='lightbulb icon outline'></i>
        <div class='menu'>
          <div class='header'>
            <i class='lightbulb icon outline'></i>
            Suggestions de commentaires
          </div>
          {{#each suggestionComments as |suggestion|}}
            <button
              class='item'
              type='button'
              {{on 'click' (fn this.appendComment suggestion)}}
              data-test-comment-suggestion={{suggestion}}
            >
              {{suggestion}}
            </button>
          {{/each}}
        </div>
      </div>
    </div>
  </template>
}
